import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  Spin,
  Statistic,
  Select,
  DatePicker,
} from "antd";
import {
  UserOutlined,
  FileTextOutlined,
  SwapOutlined,
  BankOutlined,
  ExportOutlined,
  FieldTimeOutlined,
} from "@ant-design/icons";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title as ChartTitle,
} from "chart.js";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import dayjs from "dayjs";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";

const { Text, Title } = Typography;

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ChartTitle,
);

const BASE_COLORS = [
  "#1677ff", // Primary Blue
  "#52c41a", // Success Green
  "#faad14", // Warning Gold
  "#fa541c", // Orange
  "#722ed1", // Purple
  "#13c2c2", // Cyan
  "#eb2f96", // Pink
];

const Dashboard = () => {
  const navigate = useNavigate();
  const { request } = useRequest();

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("7days");

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = () => {
    setLoading(true);
    request({
      url: `api/${apiPath.dashboard}`,
      method: "GET",
      onSuccess: (data) => {
        setDashboardData(data?.data || {});
        setLoading(false);
      },
      onError: () => setLoading(false),
      // onError: () => {
      //   setDashboardData(MOCK_DATA.data);
      //   setLoading(false);
      // }
    });
  };

  const metrics = dashboardData?.metrics || {};

  const users = metrics?.users || {};
  const adminIncome = dashboardData?.admin_income || {};

  const incomeData =
    filter === "7days"
      ? adminIncome?.last_7_days || []
      : adminIncome?.last_12_months || [];

  /* ----------------------- Charts Data ---------------------- */

  const incomeChartData = {
    labels:
      incomeData.length > 0
        ? incomeData.map((d) => d.date || d.month)
        : ["No Data"],
    datasets: [
      {
        label: "Admin Income",
        data: incomeData.length > 0 ? incomeData.map((d) => d.income) : [0],
        borderColor: "#1677ff",
        backgroundColor: "rgba(22,119,255,0.1)",
        fill: true,
        tension: 0.5,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const shadeColor = (color, percent) => {
    const num = parseInt(color.replace("#", ""), 16);
    const r = (num >> 16) + percent;
    const g = ((num >> 8) & 0x00ff) + percent;
    const b = (num & 0x0000ff) + percent;

    const newColor =
      0x1000000 +
      (Math.min(255, Math.max(0, r)) << 16) +
      (Math.min(255, Math.max(0, g)) << 8) +
      Math.min(255, Math.max(0, b));

    return "#" + newColor.toString(16).slice(1);
  };

  // ✅ Unlimited smooth color generator
  const generateColors = (count) => {
    const colors = [];

    for (let i = 0; i < count; i++) {
      const base = BASE_COLORS[i % BASE_COLORS.length];
      const shadeLevel = Math.floor(i / BASE_COLORS.length) * 20;
      colors.push({
        background: shadeColor(base, shadeLevel),
        border: shadeColor(base, shadeLevel - 20),
      });
    }

    return colors;
  };

  const userLabels = ["Individual", "Premium", "Business"];
  const userValues = [
    users?.individual || 0,
    users?.premium || 0,
    users?.business || 0,
  ];

  const userColors = generateColors(userLabels.length);
  const userDistributionData = {
    labels: userLabels,
    datasets: [
      {
        data: userValues,
        backgroundColor: userColors.map((c) => c.background),
        borderColor: userColors.map((c) => c.border),
        borderWidth: 1,
      },
    ],
  };

  const transactionLabels = ["Topups", "Withdrawals", "Pending"];

  const transactionValues = [
    metrics?.topup_transactions || 0,
    metrics?.withdrawal_transactions || 0,
    metrics?.pending_transactions || 0,
  ];

  const transactionColors = generateColors(transactionLabels.length);

  const transactionBarData = {
    labels: ["Transactions"],
    datasets: transactionLabels.map((label, index) => ({
      label,
      data: [transactionValues[index]],
      backgroundColor: transactionColors[index]?.background,
      borderColor: transactionColors[index]?.border,
      borderWidth: 1,
    })),
  };

  /* ---------------------- Reusable Card --------------------- */

  const MetricCard = ({ title, value, icon, onClick }) => (
    <Card
      hoverable
      onClick={onClick}
      style={{
      }}
      bodyStyle={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Space direction="vertical" size={4}>
        <div
          style={{
            fontSize: 26,
            color: "#1677ff",
            transition: "0.3s",
          }}
        >
          {icon}
        </div>

        <Text type="secondary" style={{ fontSize: 13 }}>
          {title}
        </Text>

        <Title level={3} style={{ margin: 0 }}>
          {value}
        </Title>
      </Space>
    </Card>
  );

  const commonChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    maxBarThickness: 50,
    animation: {
      duration: 1200,
      easing: "easeOutQuart",
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: 20,
          font: { size: 12 },

        },
      },
    },
  };

  /* --------------------------- UI --------------------------- */

  return (
    <Card>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 32 }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Admin Dashboard
          </Title>
          <Text type="secondary">
            Overview of system performance and financial metrics
          </Text>
        </div>

        {/* <Space style={{ flexWrap: "wrap" }}>
          <Button
            type={filter === "7days" ? "primary" : "default"}
            shape="round"
            onClick={() => setFilter("7days")}
          >
            Last 7 Days
          </Button>

          <Button
            type={filter === "12months" ? "primary" : "default"}
            shape="round"
            onClick={() => setFilter("12months")}
          >
            Last 12 Months
          </Button>

          <DatePicker picker="month" />
        </Space> */}
        {/* <Space style={{ flexWrap: "wrap" }}>
          <Select
            value={filter || "7days"}
            onChange={(value) => setFilter(value)}
            options={[
              { value: "7days", label: "Last 7 Days" },
              { value: "12months", label: "Last 12 Months" },
            ]}
          />
        </Space> */}
      </Row>

      {loading ? (
        <div style={{ textAlign: "center", padding: "120px 0" }}>
          <Spin size="large" />
          <div style={{ marginTop: 12 }}>
            <Text type="secondary">Loading dashboard data...</Text>
          </div>
        </div>
      ) : (
        <>
          {/* User Cards */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={8}>
              <MetricCard
                title="Total Active Users"
                value={users?.individual || 0}
                icon={<UserOutlined />}
                onClick={() => navigate("/user-manager")}
              />
            </Col>

            {/* <Col xs={24} sm={12} lg={8}>
              <MetricCard
                title="Premium Users"
                value={users?.premium || 0}
                icon={<UserOutlined />}
                onClick={() => navigate("/user-manager")}
              />
            </Col> */}

            <Col xs={24} sm={12} lg={8}>
              <MetricCard
                title="Total Active Merchants"
                value={users?.business || 0}
                icon={<UserOutlined />}
                onClick={() => navigate("/user-manager?tab=merchants")}
              />
            </Col>
          </Row>

          {/* Financial Cards */}
          <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
            <Col xs={24} sm={12} xxl={6}>
              <MetricCard
                title="Trust Account Balance"
                value={`NZ$ ${(metrics?.trust_balance || 0).toLocaleString()}`}
                icon={<BankOutlined />}
                onClick={() => navigate("/trust-account-manager")}
              />
            </Col>

            <Col xs={24} sm={12} xxl={6}>
              <MetricCard
                title="Top-Up Transactions"
                value={metrics?.topup_transactions || 0}
                icon={<SwapOutlined />}
                onClick={() => navigate("/transaction-manager")}
              />
            </Col>

            <Col xs={24} sm={12} xxl={6}>
              <MetricCard
                title="Withdrawals"
                value={metrics?.approved_withdrawals || 0}
                icon={<ExportOutlined />}
                onClick={() => navigate("/withdrawal-request-manager")}
              />
            </Col>

            <Col xs={24} sm={12} xxl={6}>
              <MetricCard
                title="Pending Transactions"
                value={metrics?.pending_transactions || 0}
                icon={<FieldTimeOutlined />}
                onClick={() => navigate("/transaction-manager")}
              />
            </Col>
          </Row>

          {/* Charts Section */}
          <Row gutter={[16, 16]} style={{ marginTop: 30 }}>
            <Col xs={24} lg={12}>
              <Card
                style={{
                  borderRadius: 20,
                  boxShadow: "0 8px 30px rgba(0,0,0,0.06)",
                }}
                title={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <span>Admin Income</span>

                    <Select
                      value={filter || "7days"}
                      onChange={(value) => setFilter(value)}
                      options={[
                        { value: "7days", label: "Last 7 Days" },
                        { value: "12months", label: "Last 12 Months" },
                      ]}
                      style={{ minWidth: 150 }}
                    />
                  </div>
                }
              >
                <div style={{ height: 300 }}>
                  <Line data={incomeChartData} options={commonChartOptions} />
                </div>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card title="User Distribution" style={{ borderRadius: 16 }}>
                <div style={{ height: 300 }}>
                  <Doughnut
                    data={userDistributionData}
                    options={commonChartOptions}
                  />
                </div>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
            <Col xs={24}>
              <Card title="Transaction Overview" style={{ borderRadius: 16 }}>
                <div style={{ height: 300 }}>
                  <Bar data={transactionBarData} options={commonChartOptions} />
                </div>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Card>
  );
};

export default Dashboard;
